import { downloadFile } from "@/utils/downloadFile";
import {
  CertificateData,
  certificateFileName,
  certificatesBundleFileName,
  CertificateSignature,
} from "./certificateData";

/**
 * Generates one PDF holding a page per certificate and triggers a browser
 * download. A single item downloads under that member's filename; multiple
 * items download as one bundle the user can print in a single pass.
 *
 * The ~1 MB @react-pdf/renderer library and the certificate template (with its
 * bundled fonts) are dynamically imported so they stay out of the Leaderboards
 * bundle until a certificate is actually requested.
 *
 * `signature` optionally overrides the signer: an uploaded PNG (data URL,
 * browser-only, never persisted) and/or the printed signature name line.
 */
export const downloadCertificates = async (
  items: CertificateData[],
  signature?: CertificateSignature,
): Promise<void> => {
  if (items.length === 0) {
    return;
  }

  const [{ pdf }, { default: CertificatesDocument }] = await Promise.all([
    import("@react-pdf/renderer"),
    import("./CertificatePdf"),
  ]);

  const blob = await pdf(
    <CertificatesDocument items={items} signature={signature} />,
  ).toBlob();

  const fileName =
    items.length === 1
      ? certificateFileName(items[0])
      : certificatesBundleFileName(items[0].month);

  downloadFile(blob, fileName);
};
